/**
 * GCFacilitiesR2Service — Geocodificador L1 para instalaciones Guardia Civil
 *
 * Busca puestos, compañías y subsectores de la Guardia Civil en Andalucía
 * almacenados en R2 mediante fuzzy matching con uFuzzy.
 *
 * Datos: 629 instalaciones en 413 municipios — Guardia Civil Datos Abiertos
 * CRS: EPSG:25830 (UTM30 ETRS89, geocodificado y reproyectado desde WGS84)
 * Ruta R2: /gc-facilities/{ine5}.json.gz
 * Precisión: ±20 m CartoCiudad / ±50 m Nominatim
 * Licencia: Datos abiertos reutilizables
 *
 * Los tokens uFuzzy están PRE-COMPUTADOS en cada feature (campo "tokens"),
 * por lo que se usan directamente como haystack sin recalcular.
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-23
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  GCFacilityFeature,
  GCFacilitySearchOptions,
  GCFacilityResult,
} from '../../../types/gcFacilities';
import {
  GC_X_RANGE,
  GC_Y_RANGE,
  GC_PRECISION_CARTOCIUDAD_M,
} from '../../../types/gcFacilities';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;
const BASE_CONFIDENCE = 75; // Datos oficiales Guardia Civil

/** Configuración uFuzzy para nombres españoles de instalaciones GC */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: GCFacilitiesR2Service | null = null;

export function getGCFacilitiesR2Service(): GCFacilitiesR2Service {
  if (!serviceInstance) {
    serviceInstance = new GCFacilitiesR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetGCFacilitiesR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class GCFacilitiesR2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: GCFacilityFeature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<GCFacilityFeature[]>>();
  private readonly fuzzy: uFuzzy;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga instalaciones GC de un municipio desde R2 (con caché LRU) */
  async loadMunicipio(codINE: string): Promise<GCFacilityFeature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    // Cache hit
    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    // Dedup concurrent requests
    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<GCFacilityFeature[]> {
    const url = `${this.baseUrl}/gc-facilities/${codINE}.json.gz`;

    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (response.status === 404) {
        // Municipio sin instalaciones GC — ~370 municipios no tienen
        return [];
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: unknown = await response.json();
      if (!Array.isArray(data)) {
        console.warn(`[GC_R2] Respuesta no es array para ${codINE}:`, typeof data);
        return [];
      }
      return (data as GCFacilityFeature[]).filter(f => this.isValidCoord(f));
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[GC_R2] Timeout cargando ${codINE}`);
      } else {
        console.error(`[GC_R2] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica una instalación de la Guardia Civil buscando en datos R2.
   * Retorna el mejor resultado o null.
   */
  async geocode(options: GCFacilitySearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name ?? '');
  }

  /**
   * Busca instalaciones GC por nombre fuzzy y/o subtipo.
   * Usa campo "tokens" pre-computado como haystack para uFuzzy.
   * Retorna array ordenado por confianza descendente.
   */
  async search(options: GCFacilitySearchOptions): Promise<GCFacilityResult[]> {
    const { codINE, name, subtipo, minConfidence = 55, maxResults = 5 } = options;

    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    // Filtrar por subtipo si especificado
    let candidates = features;
    if (subtipo) {
      candidates = candidates.filter(
        f => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
      if (candidates.length === 0) return [];
    }

    // Sin nombre → devolver candidatos filtrados con confianza base
    if (!name?.trim()) {
      return candidates
        .slice(0, maxResults)
        .map(f => this.toResult(f, BASE_CONFIDENCE));
    }

    // Normalizar nombre: eliminar palabras genéricas de seguridad
    const normalizedName = this.stripSecurityNoise(name);

    // Búsqueda fuzzy usando tokens pre-computados
    return this.fuzzySearch(candidates, normalizedName, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // NORMALIZACIÓN DE NOMBRE (eliminar ruido genérico)
  // ---------------------------------------------------------------------------

  /** Elimina palabras clave genéricas de cuerpos de seguridad del nombre de búsqueda */
  private stripSecurityNoise(name: string): string {
    const noisePatterns = [
      /\bguardia\s+civil\b/gi,
      /\bpolic[ií]a\s+(local|nacional|municipal)\b/gi,
      /\bbomberos?\b/gi,
      /\bproteccion\s+civil\b/gi,
      /\bcuerpo\s+de\b/gi,
      /\bcomisaria?\b/gi,
      /\bcuartel\b/gi,
      /\bparque\s+de\s+bomberos?\b/gi,
    ];
    let cleaned = name;
    for (const pattern of noisePatterns) {
      cleaned = cleaned.replace(pattern, '');
    }
    // Colapsar espacios múltiples y trim
    return cleaned.replace(/\s+/g, ' ').trim();
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING (usando tokens pre-computados)
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: GCFacilityFeature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): GCFacilityResult[] {
    // CLAVE: usar tokens pre-computados como haystack
    const haystack = candidates.map(f => f.tokens);
    const normalizedName = searchName.toLowerCase().trim();
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: GCFacilityResult[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature.tokens, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  /** Calcula score normalizado 0-1 del fuzzy match (contra tokens y nombre) */
  private calcFuzzyScore(needle: string, tokens: string, nombre: string): number {
    const nLow = nombre.toLowerCase();
    const tLow = tokens.toLowerCase();
    if (tLow === needle || nLow === needle) return 1.0;
    if (tLow.includes(needle) || nLow.includes(needle)) return 0.95;
    if (needle.includes(tLow) || needle.includes(nLow)) return 0.90;
    const ratio = needle.length / Math.max(tLow.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  /**
   * Convierte fuzzy score (0-1) a confianza (0-100).
   * Techo 85: datos oficiales Guardia Civil geocodificados por CartoCiudad/Nominatim.
   * Más fiable que OSM (techo 80) por ser fuente oficial.
   */
  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 85;  // Oficial GC: techo 85
    if (score >= 0.85) return 80;
    if (score >= 0.70) return 72;
    if (score >= 0.55) return 65;
    if (score >= 0.40) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN Y VALIDACIÓN
  // ---------------------------------------------------------------------------

  /** Convierte feature a resultado intermedio con confianza */
  private toResult(
    feature: GCFacilityFeature,
    confidence: number,
    fuzzyScore?: number,
  ): GCFacilityResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: GC_PRECISION_CARTOCIUDAD_M,
      source: 'gc_facilities_r2',
      feature,
      fuzzyScore,
      matchedName: feature.nombre,
    };
  }

  /** Convierte resultado GC a GeocodingResult estándar */
  toGeocodingResult(
    result: GCFacilityResult,
    _searchName: string,
  ): GeocodingResult {
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'gc_facilities_r2',
      matchedName: result.matchedName,
    };
  }

  /** Valida que las coordenadas estén dentro del rango esperado (Andalucía UTM30) */
  private isValidCoord(feature: GCFacilityFeature): boolean {
    return (
      feature.x >= GC_X_RANGE.min &&
      feature.x <= GC_X_RANGE.max &&
      feature.y >= GC_Y_RANGE.min &&
      feature.y <= GC_Y_RANGE.max
    );
  }

  // ---------------------------------------------------------------------------
  // CACHÉ LRU
  // ---------------------------------------------------------------------------

  /** Inserta en caché con evicción LRU si se excede el límite */
  private setCache(codINE: string, data: GCFacilityFeature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      let oldestKey: string | null = null;
      let oldestTs = Infinity;
      for (const [key, entry] of this.cache) {
        if (entry.ts < oldestTs) {
          oldestTs = entry.ts;
          oldestKey = key;
        }
      }
      if (oldestKey) this.cache.delete(oldestKey);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  /** Limpia toda la caché */
  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Número de entradas en caché (útil para tests) */
  get cacheSize(): number {
    return this.cache.size;
  }

  /** Carga features directamente en caché sin fetch (útil para tests) */
  loadFromData(codINE: string, features: GCFacilityFeature[]): void {
    this.setCache(codINE, features);
  }
}
